import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * 合并 className。
 * clsx 负责条件拼接，tailwind-merge 负责去掉互相冲突的类
 * （比如外部传进来的 px-6 能正确覆盖组件内置的 px-4，而不是两个都留着看谁写在后面）。
 *
 * ---------------------------------------------------------------------------
 * 为什么必须把自定义令牌注册给 tailwind-merge
 *
 * tokens.css 用 Tailwind v4 的 @theme 定义了一套自己的字号（text-body、
 * text-card-title…）、字间距和圆角。这些名字 Tailwind 认得，但 tailwind-merge
 * **不认得**——它只内置了官方那套 text-sm / text-lg。
 *
 * 而 `text-*` 这个前缀是有歧义的：既可能是字号（text-sm）也可能是颜色
 * （text-red-500）。tailwind-merge 认不出 `text-body` 是字号时，就把它当成颜色，
 * 于是 `text-white` 和 `text-body` 被判成同一组冲突类，**后面的赢，前面的被删掉**。
 *
 * 结果是 Button 的 `bg-brand text-white` + `text-body` 合并后 text-white 消失，
 * 主按钮从 global.css 的 `button { color: inherit }` 继承出黑色文字；
 * NoteBox 和 Tag 则反过来丢掉字号。整个应用的按钮、标签、说明框都受影响。
 *
 * 所以下面这份配置不是"优化"，是让 cn() 正确工作的前提。
 * 往 tokens.css 里加新的字号或字间距时，必须同步加到这里，否则又会开始悄悄丢类。
 */

/** 和 tokens.css 的 --text-* 一一对应 */
const FONT_SIZES = [
  'page-title',
  'modal-title',
  'card-title',
  'amount',
  'body',
  'table',
  'form',
  'note',
  'tiny',
  'chip',
  'group',
];

/** 和 tokens.css 的 --tracking-* 一一对应 */
const TRACKINGS = ['title', 'amount', 'group', 'eyebrow'];

/** 和 tokens.css 的 --radius-* 一一对应（btn / tile 是官方没有的） */
const RADII = ['btn', 'tile'];

/** 和 tokens.css 的 --shadow-* 一一对应 */
const SHADOWS = ['e1', 'e2', 'e3', 'e4', 'brand'];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: FONT_SIZES }],
      tracking: [{ tracking: TRACKINGS }],
      rounded: [{ rounded: RADII }],
      'shadow-color': [],
      shadow: [{ shadow: SHADOWS }],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
